import React, { createContext, useContext, useEffect, useState } from 'react'
import { useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser, selectIsAuthenticated, selectIsOfflineMode } from '@/redux/reducers/auth.reducer'
import { selectAppSettings } from '@/redux/reducers/app.reducer'
import { offlineDatabase, encryptionService, syncService } from '@/core/offline'
import { OFFLINE_AUTH_KEY } from '@/constants/offline-auth'

type OfflineModeContextType = {
  isOfflineMode: boolean
  canAccessOfflineMode: boolean
  lastOnlineSync: Date | null
  timeUntilExpiry: number | null // hours until offline access expires
  isOfflineDatabaseReady: boolean
}

const OfflineModeContext = createContext<OfflineModeContextType>({
  isOfflineMode: false,
  canAccessOfflineMode: false,
  lastOnlineSync: null,
  timeUntilExpiry: null,
  isOfflineDatabaseReady: false,
})

export const useOfflineMode = () => useContext(OfflineModeContext)

const OFFLINE_ACCESS_DURATION_HOURS = 48
const LAST_ONLINE_SYNC_KEY = 'offline_last_sync'
const OFFLINE_DB_INITIALIZED_KEY = 'offline_db_initialized'

// Wrapper component that conditionally uses Keycloak
function OfflineModeProviderInner({ children }: { children: React.ReactNode }) {
  const [isOfflineMode, setIsOfflineMode] = useState(!navigator.onLine)
  const [lastOnlineSync, setLastOnlineSync] = useState<Date | null>(null)
  const [isOfflineDatabaseReady, setIsOfflineDatabaseReady] = useState(false)

  const authUser = useAppSelector(selectAuthUser)
  const isAuthenticated = useAppSelector(selectIsAuthenticated)
  const isReduxOfflineMode = useAppSelector(selectIsOfflineMode)
  const appSettings = useAppSelector(selectAppSettings)

  // Do not use Keycloak hook here to avoid rules-of-hooks violations across modes
  // Rely on Redux auth state and local storage only

  // Initialize last online sync from localStorage
  useEffect(() => {
    const storedLastSync = localStorage.getItem(LAST_ONLINE_SYNC_KEY)
    if (storedLastSync) {
      setLastOnlineSync(new Date(storedLastSync))
    }
  }, [])

  // Initialize offline database when authenticated
  useEffect(() => {
    const initializeOfflineDatabase = async () => {
      if (!isAuthenticated || !authUser) {
        return
      }

      try {
        // Check if database was already initialized for this user
        const dbInitialized = localStorage.getItem(`${OFFLINE_DB_INITIALIZED_KEY}_${authUser._id}`)

        if (!dbInitialized) {
          // Initialize encryption with user-specific key
          const encryptionKey = isReduxOfflineMode ? `${authUser._id}_offline` : `${authUser._id}_online`
          await encryptionService.initialize(encryptionKey)

          // Initialize database
          await offlineDatabase.initialize()

          // Mark as initialized for this user
          localStorage.setItem(`${OFFLINE_DB_INITIALIZED_KEY}_${authUser._id}`, 'true')
        } else {
          // Database already initialized, just reconnect
          const encryptionKey = isReduxOfflineMode ? `${authUser._id}_offline` : `${authUser._id}_online`
          await encryptionService.initialize(encryptionKey)
          await offlineDatabase.initialize()
        }

        setIsOfflineDatabaseReady(true)
      } catch (error) {
        console.error('Failed to initialize offline database:', error)
        setIsOfflineDatabaseReady(false)
      }
    }

    initializeOfflineDatabase()
  }, [isAuthenticated, authUser, isReduxOfflineMode])

  // Clean up database on logout
  useEffect(() => {
    if (!isAuthenticated && isOfflineDatabaseReady) {
      offlineDatabase.close()
      setIsOfflineDatabaseReady(false)
    }
  }, [isAuthenticated, isOfflineDatabaseReady])

  // When DB becomes ready and we are online, trigger a smart sync once
  useEffect(() => {
    if (isOfflineDatabaseReady && navigator.onLine) {
      try {
        // Scan, re-queue failed items, then sync
        syncService.syncNow()
      } catch (e) {
        console.warn('Failed to trigger sync after DB ready:', e)
      }
    }
  }, [isOfflineDatabaseReady])

  // Monitor online/offline status
  useEffect(() => {
    const handleOnline = () => {
      setIsOfflineMode(false)
      const now = new Date()
      setLastOnlineSync(now)
      localStorage.setItem(LAST_ONLINE_SYNC_KEY, now.toISOString())
    }

    const handleOffline = () => {
      // Only auto-switch to offline mode if the setting is enabled
      if (appSettings.autoSwitchOfflineMode) {
        setIsOfflineMode(true)
      }
    }

    // Initial check - only set offline mode if auto-switch is enabled
    if (!navigator.onLine && appSettings.autoSwitchOfflineMode) {
      setIsOfflineMode(true)
    } else if (navigator.onLine) {
      setIsOfflineMode(false)
    }

    if (navigator.onLine && !isReduxOfflineMode) {
      handleOnline()
    }

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [isReduxOfflineMode, appSettings.autoSwitchOfflineMode])

  // Calculate time until offline access expires
  const timeUntilExpiry = React.useMemo(() => {
    if (!lastOnlineSync) return null

    const hoursSinceLastSync = (new Date().getTime() - lastOnlineSync.getTime()) / (1000 * 60 * 60)
    const remainingHours = OFFLINE_ACCESS_DURATION_HOURS - hoursSinceLastSync

    return Math.max(0, remainingHours)
  }, [lastOnlineSync])

  // Check if user can access offline mode
  const canAccessOfflineMode = React.useMemo(() => {
    // Must be authenticated
    if (!isAuthenticated || !authUser) return false

    // In offline mode, we don't check Keycloak
    if (isReduxOfflineMode) {
      // Just check if we have cached auth and it's within the time window
      const offlineAuthData = localStorage.getItem(OFFLINE_AUTH_KEY)
      if (!offlineAuthData) return false

      try {
        const authData = JSON.parse(offlineAuthData)
        const lastAuth = new Date(authData.lastOnlineAuth)
        const hoursSinceAuth = (Date.now() - lastAuth.getTime()) / (1000 * 60 * 60)
        return hoursSinceAuth < OFFLINE_ACCESS_DURATION_HOURS
      } catch {
        return false
      }
    }

    // Database must be ready
    if (!isOfflineDatabaseReady) return false

    // Check if we're within the 48-hour offline window
    if (lastOnlineSync) {
      const hoursSinceLastSync = (new Date().getTime() - lastOnlineSync.getTime()) / (1000 * 60 * 60)
      if (hoursSinceLastSync > OFFLINE_ACCESS_DURATION_HOURS) {
        return false
      }
    } else if (!navigator.onLine) {
      // If we've never been online in this session and we're offline, deny access
      return false
    }

    return true
  }, [isAuthenticated, authUser, lastOnlineSync, isOfflineDatabaseReady, isReduxOfflineMode])

  return (
    <OfflineModeContext.Provider
      value={{
        isOfflineMode: isOfflineMode || isReduxOfflineMode,
        canAccessOfflineMode,
        lastOnlineSync,
        timeUntilExpiry,
        isOfflineDatabaseReady,
      }}
    >
      {children}
    </OfflineModeContext.Provider>
  )
}

// Main provider that handles the conditional rendering
export function OfflineModeProvider({ children }: { children: React.ReactNode }) {
  const isReduxOfflineMode = useAppSelector(selectIsOfflineMode)

  // If we're in offline mode, render the provider without Keycloak context
  // Otherwise, it will be rendered inside Keycloak context in the app
  return <OfflineModeProviderInner>{children}</OfflineModeProviderInner>
}
